/**
 * WebEmbed - custom callback-based screen with iframe and Shadow DOM support
 * V2 of CustomScreen - uses EmbedConfig (callback/inputs/outputs) instead of raw labels/values
 */

import { serializeCustomScreenChildSections as serializeChildSectionsForPreview, findCustomScreenChildSection } from '@modules/screen-view-v2/customScreenDataSources';
import React, { memo, useRef, useEffect, useMemo } from 'react';
import type { CSSProperties } from 'react';
import type { StepNode } from '@segmently/schema-models/v2';

// Legacy types for SDK backward compatibility (EmbedInput/EmbedOutput removed from schema v2.16)
interface LegacyEmbedInput { name: string; inputType: string; value: string; variableId?: string; }
interface LegacyEmbedOutput { name: string; value?: { translations?: Record<string, string> }; }
import type { OnActionCallback, OnEventCallback } from '../../../types/events';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { resolveLabel } from '../../../utils/contentResolver';
import { resolveEmbedColors } from '../../../utils/canvasForeground';

import { ActionBar } from '../../shared/ActionBar';
import { Background } from '../../shared/Background';
import { HeaderBar } from '../../shared/HeaderBar';
import styles from './WebEmbed.module.css';

import { IframeRenderer } from './IframeRenderer';
import type {
  CustomScreenButtonActionRequest,
  CustomScreenOptionActionRequest,
  CustomScreenRenderMode,
  LegacyInitData,
} from '../../../types/customScreenBridge.types';
import { buildShadowDomSdk, type ShadowDomSdkInstance } from './buildShadowDomSdk';
import { useCustomPurchaseSlots } from '@modules/screen-view-v2/components/screens/WebEmbed/useCustomPurchaseSlots';

/**
 * Custom render callback type (V2: uses StepNode instead of Screen)
 */
export type CustomRenderCallback = (step: StepNode, locale: string) => React.ReactNode;

export interface WebEmbedProps {
  step: StepNode;
  /** A Flexible CustomEmbed already owns its layout; omit standalone screen chrome. */
  embedded?: boolean;
  locale: string;
  variables?: TemplateVariables;
  resolvedScreenValues?: Record<string, string>;
  onAction?: OnActionCallback;
  onEvent?: OnEventCallback;
  editMode?: boolean;
  previewMode?: PreviewMode;
  /** Raw HTML content for iframe/shadow-dom rendering modes */
  htmlContent?: string;
  /** React callback for custom content (legacy mode) */
  customRender?: CustomRenderCallback;
  /** Callback for two-way binding via sdk.setValue() */
  onValueChange?: (key: string, value: unknown) => void;
  /** Render mode: 'iframe' (default, full isolation) or 'shadow-dom' (CSS isolation) */
  renderMode?: CustomScreenRenderMode;
  /** Init data for iframe mode (runtime data from FunnelEngine) */
  initData?: LegacyInitData;
  /** Callback when iframe requests forward navigation */
  onNavigate?: (data: Record<string, unknown>) => void;
  /** Callback when iframe requests back navigation */
  onBack?: () => void;
  /** Callback for analytics events from iframe */
  onTrackEvent?: (name: string, params?: Record<string, unknown>) => void;
  /** Callback when iframe sets a variable value via sdk.setVariable() */
  onSetVariable?: (variableId: string, value: unknown) => void;
  /** Callback when custom HTML triggers an option action on a child section */
  onTriggerOptionAction?: (request: CustomScreenOptionActionRequest) => void;
  /** Callback when custom HTML triggers a Button child section action */
  onTriggerButtonAction?: (request: CustomScreenButtonActionRequest) => void;
  /** Variable update broadcast for cross-section reactivity */
  lastVariableUpdate?: { variableId: string; value: unknown; sourceIframeId?: string; _ts: number } | null;
  /** Callback when the SDK changes the selected child catalog product. */
  onSelectProduct?: (childSectionId: string, productIndex: number, productId: string) => void;
  /** Callback when iframe requests a purchase via sdk.purchaseProduct() */
  onPurchaseProduct?: (requestId: string, productId: string, childSectionId?: string) => void;
  /** Purchase result to push back to iframe (from host payment flow) */
  lastPurchaseResult?: { requestId: string; status: 'success' | 'cancel' | 'error'; error?: string; _ts: number } | null;
  /**
   * True when rendering via react-dom/server (V2 SSR pipeline). In shadow-dom mode the
   * render switches to Declarative Shadow DOM (`<template shadowrootmode="open">…</template>`)
   * so the browser materialises the ShadowRoot on HTML parse — no post-hydration empty-div
   * FOUC, and Apple Pay sees shadow-host-owned context immediately. No-op for iframe mode.
   */
  ssrMode?: boolean;
}

/**
 * Transform V2 EmbedOutput to SDK labels format (flat key → localized string)
 */
function transformOutputsForSDK(
  outputs: Record<string, LegacyEmbedOutput> | undefined,
  locale: string
): Record<string, string> {
  if (!outputs) return {};

  const result: Record<string, string> = {};
  Object.entries(outputs).forEach(([key, output]) => {
    result[key] = resolveLabel(output.value as any, locale) || '';
  });
  return result;
}

/**
 * Transform V2 EmbedOutput to legacy nested format for onboarding.online compatibility
 */
export function transformOutputsToLegacyFormat(
  outputs: Record<string, LegacyEmbedOutput> | undefined
): Record<string, { value: { l10n: Record<string, string> } }> {
  if (!outputs) return {};

  const result: Record<string, { value: { l10n: Record<string, string> } }> = {};
  Object.entries(outputs).forEach(([key, output]) => {
    // V2 LocalizedLabel stores locale map in .translations
    const translations = output.value?.translations;
    if (translations) {
      result[key] = { value: { l10n: translations } };
    }
  });
  return result;
}

/**
 * Transform V2 EmbedInput values to SDK format with type parsing
 */
export function transformInputsForSDK(
  inputs: Record<string, LegacyEmbedInput> | undefined
): Record<string, unknown> {
  if (!inputs) return {};

  const result: Record<string, unknown> = {};
  Object.entries(inputs).forEach(([key, input]) => {
    const strValue = input.value || '';
    switch (input.inputType) {
      case 'int':
        result[key] = parseInt(strValue, 10) || 0;
        break;
      case 'double':
        result[key] = parseFloat(strValue) || 0;
        break;
      case 'date':
        result[key] = strValue;
        break;
      case 'listString':
        result[key] = strValue.split(',').map(s => s.trim());
        break;
      case 'listInt':
        result[key] = strValue.split(',').map(s => parseInt(s.trim(), 10) || 0);
        break;
      case 'listDouble':
        result[key] = strValue.split(',').map(s => parseFloat(s.trim()) || 0);
        break;
      default:
        result[key] = strValue;
    }
  });
  return result;
}

/**
 * Create mock LegacyInitData for preview/edit mode
 */
function createPreviewInitData(
  step: StepNode,
  locale: string,
  embed: StepNode['content']['embed']
): LegacyInitData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const embedAny = embed as any;
  const initData: LegacyInitData = {
    inputs: {},
    labels: embedAny?.outputs ? transformOutputsToLegacyFormat(embedAny.outputs) : {},
    userData: {},
    customData: {},
    webOnboarding: {
      onboardingId: 'preview',
      versionId: undefined,
      screenCount: undefined,
    },
    incomeUserData: {
      ip: null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    },
    language: locale,
    screenId: step.id,
    screenName: (step as unknown as Record<string, unknown>).name as string | undefined,
    values: embedAny?.inputs ? transformInputsForSDK(embedAny.inputs) : {},
  };

  // Serialize child sections for preview (without price resolution)
  if (Array.isArray(embedAny?.childSections) && embedAny.childSections.length > 0) {
    initData.childSections = serializeChildSectionsForPreview(embedAny.childSections, locale);
  }

  return initData;
}

/**
 * Activate scripts in Shadow DOM.
 * innerHTML doesn't execute script tags, so we replace them programmatically.
 *
 * Each replaced script is prefixed with a per-script DOM proxy IIFE that:
 *   - Routes document.getElementById / querySelector / querySelectorAll to the
 *     shadow root first (with main document fallback).
 *   - Restores the per-shadow SDK snapshot before the script body runs (for
 *     multi-shadow canvas pages where each screen has its own SDK instance).
 *
 * WHY NO createElement/createElementNS/appendChild PROXIES:
 * We deliberately do NOT proxy element creation or append operations. React's
 * reconciler uses `document.createElement` + `appendChild` internally for its
 * entire fiber tree. Monkey-patching those globally causes React's unmount
 * phase to throw `Failed to execute 'removeChild': The node to be removed is
 * not a child of this node` because any redirection would desync React's
 * fiber parent tracking from actual DOM parent relationships.
 *
 * As a result, custom HTML scripts cannot safely use
 * `document.createElement + document.body.appendChild` to inject content —
 * those elements would leak out of the shadow root. The canonical solution
 * is to write repeatable content as STATIC HTML markup (rule #6 in the
 * figma-screen-converter agent, Step 4.5). Static markup is injected via
 * innerHTML before scripts run and automatically lives inside the shadow root.
 */
function activateScripts(shadow: ShadowRoot, shadowId: string): void {
  (window as any)[shadowId] = shadow;

  shadow.querySelectorAll('script').forEach(oldScript => {
    const newScript = document.createElement('script');
    Array.from(oldScript.attributes).forEach(attr => {
      newScript.setAttribute(attr.name, attr.value);
    });

    const originalContent = oldScript.textContent || '';
    const domProxies = `
      (function() {
        var __shadowRoot = window['${shadowId}'];
        var __origGetElementById = document.getElementById.bind(document);
        var __origQuerySelector = document.querySelector.bind(document);
        var __origQuerySelectorAll = document.querySelectorAll.bind(document);

        // Query proxies — shadow root first, main document fallback.
        // We intentionally do NOT proxy createElement, createElementNS, or
        // appendChild here: React's reconciler uses those internally and
        // any redirection breaks fiber-tree ↔ DOM parent sync, causing
        // removeChild crashes on unmount.
        document.getElementById = function(id) {
          return __shadowRoot.getElementById(id) || __origGetElementById(id);
        };
        document.querySelector = function(sel) {
          return __shadowRoot.querySelector(sel) || __origQuerySelector(sel);
        };
        document.querySelectorAll = function(sel) {
          var shadowResults = __shadowRoot.querySelectorAll(sel);
          return shadowResults.length > 0 ? shadowResults : __origQuerySelectorAll(sel);
        };

        // Per-shadow SDK: restore this screen's SDK before script executes.
        // Multiple Shadow DOMs on canvas each get isolated segmentlySDK.
        var __perShadowSdk = window['${shadowId}__sdk'];
        if (__perShadowSdk) {
          window.segmentlySDK = __perShadowSdk;
        }
      })();
    `;

    newScript.textContent = `${domProxies}\n${originalContent}`;
    oldScript.parentNode?.replaceChild(newScript, oldScript);
  });
}

export const WebEmbed = memo(function WebEmbed({
  step,
  embedded = false,
  locale,
  variables,
  resolvedScreenValues: _resolvedScreenValues,
  onAction,
  onEvent: _onEvent,
  editMode = false,
  previewMode,
  htmlContent,
  customRender,
  onValueChange,
  renderMode,
  initData,
  onNavigate,
  onBack,
  onTrackEvent,
  onSetVariable,
  onTriggerOptionAction,
  onTriggerButtonAction,
  lastVariableUpdate,
  onPurchaseProduct,
  onSelectProduct,
  lastPurchaseResult,
  ssrMode,
}: WebEmbedProps): React.ReactElement {
  const { content } = step;
  const embed = content?.embed;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const embedAny = embed as any; // Legacy access for inputs/outputs (removed from typed EmbedConfig in v2.16)

  // Derive effective render mode from isIframe flag (default: true → iframe)
  const isIframe = embed?.isIframe !== false;
  const effectiveRenderMode = renderMode ?? (isIframe ? 'iframe' : 'shadow-dom');

  // WP8 UX M-3: an embed inherits nothing — an iframe is a separate document and the
  // shadow host declares `all: initial` — so without a ground of its own the funnel's
  // canvas shows through and the embed's own dark text is unreadable on it. The runtime
  // supplies one, and exposes it as tokens so generated CSS can consume the theme.
  const embedColors = useMemo(() => resolveEmbedColors(step), [step]);


  // Get raw HTML content
  const rawHtml = htmlContent || '';
  const hasRawHtml = rawHtml.trim().length > 0 && rawHtml.includes('<');

  // Check if header/actionBar have actual visible elements (mirrors HeaderBar/ActionBar internal logic)
  const showHeader = Boolean(
    content?.header && (
      content.header.back || content.header.skip || content.header.close ||
      content.header.progressIndicator
    )
  );
  const showActionBar = Boolean(
    content?.actionBar && (content.actionBar.primary || content.actionBar.secondary)
  );

  // Read inside the shadow-DOM effect without widening its dependency list — the effect
  // owns script activation and must not re-run just because a colour was recomputed.
  const embedColorsRef = useRef(embedColors);
  embedColorsRef.current = embedColors;

  // Shadow DOM refs
  const shadowHostRef = useRef<HTMLDivElement>(null);
  const customPurchaseSlots = useCustomPurchaseSlots(step.id, shadowHostRef);
  const mountPurchaseSlotRef = useRef(customPurchaseSlots.mount); mountPurchaseSlotRef.current = customPurchaseSlots.mount;
  const shadowIdRef = useRef<string>(`__shadow_${step.id}`);
  const shadowSdkInstanceRef = useRef<ShadowDomSdkInstance | null>(null);
  // Guard: prevent script re-activation on re-renders (causes infinite loop if scripts call purchaseProduct)
  // Reset ONLY on actual screen change (step.id), NOT on StrictMode cleanup
  const shadowScriptsActivatedRef = useRef(false);
  const prevStepIdRef = useRef(step.id);
  if (prevStepIdRef.current !== step.id) {
    prevStepIdRef.current = step.id;
    shadowScriptsActivatedRef.current = false; // Real screen change — allow re-init
  }

  // CRITICAL: Store initData and callbacks in refs to avoid re-init loop.
  // Shadow DOM useEffect must NOT depend on initData (object changes every render)
  // or callback refs (recreated on state changes). SDK reads from refs at call time.
  //
  // FIX: Detect stale initData from a previous WebEmbed screen. FunnelEngineV2 updates
  // customScreenInitData via useEffect (runs AFTER render), so the first render after
  // a screen change still carries the previous screen's initData. Without this check,
  // the shadow DOM SDK would be built with stale childSections (e.g. missing ProductCatalog).
  const isInitDataCurrent = initData?.screenId === step.id;
  const effectiveInitData = isInitDataCurrent ? initData : undefined;
  const initDataRef = useRef(effectiveInitData);
  initDataRef.current = effectiveInitData;
  const callbacksRef = useRef({ onNavigate, onBack, onTrackEvent, onValueChange, onSetVariable, onTriggerOptionAction, onTriggerButtonAction, onPurchaseProduct, onSelectProduct });
  callbacksRef.current = { onNavigate, onBack, onTrackEvent, onValueChange, onSetVariable, onTriggerOptionAction, onTriggerButtonAction, onPurchaseProduct, onSelectProduct };
  const hasInitData = !!effectiveInitData;

  // Preview data ref — updated every render so fallback SDK methods always read fresh data.
  // Same trampoline pattern as callbacksRef: SDK methods read from ref at call time.
  const previewDataRef = useRef({
    childSections: embedAny?.childSections ? serializeChildSectionsForPreview(embedAny.childSections, locale) : [] as ReturnType<typeof serializeChildSectionsForPreview>,
    labels: transformOutputsForSDK(embedAny?.outputs, locale),
    values: transformInputsForSDK(embedAny?.inputs),
  });
  previewDataRef.current = {
    childSections: embedAny?.childSections ? serializeChildSectionsForPreview(embedAny.childSections, locale) : [],
    labels: transformOutputsForSDK(embedAny?.outputs, locale),
    values: transformInputsForSDK(embedAny?.inputs),
  };

  // Shadow DOM initialization effect
  useEffect(() => {
    if (!hasRawHtml || effectiveRenderMode !== 'shadow-dom' || !shadowHostRef.current) return;

    const host = shadowHostRef.current;
    let shadow = host.shadowRoot;

    // ── DSD path (modern browsers) ────────────────────────────────────────────
    // If the SSR HTML contained `<template shadowrootmode="open">…</template>` and the
    // browser supports Declarative Shadow DOM (Safari 16.4+, Chrome 111+, Firefox 123+),
    // `host.shadowRoot` is already populated by the HTML parser — Custom HTML is visible
    // before any React code ran. We just need to activate `<script>` tags (see below).
    //
    // ── DSD fallback path (Safari 16.3- and older Chromium) ───────────────────
    // Old browsers leave `<template shadowrootmode>` as an inert `<template>` element —
    // shadowRoot is null, content sits hidden inside the template. Detect and upgrade
    // manually: attach shadow + move template content into it + remove the template.
    if (!shadow) {
      const dsdTemplate = host.querySelector(
        'template[shadowrootmode]'
      ) as HTMLTemplateElement | null;
      if (dsdTemplate) {
        shadow = host.attachShadow({ mode: 'open', delegatesFocus: true });
        shadow.appendChild(dsdTemplate.content);
        dsdTemplate.remove();
      } else {
        // Client-only (no SSR): attach empty shadow, HTML injection happens below.
        shadow = host.attachShadow({ mode: 'open', delegatesFocus: true });
      }
    }

    // DSD already wrote the Custom HTML into shadow root — skip re-injection below.
    // Otherwise it'd blow away the DSD content and cause a visible flash.
    const wasDSDRendered =
      host.hasAttribute('data-dsd') && shadow.childElementCount > 0;

    // Runtime mode: full SDK via buildShadowDomSdk (initData available from FunnelEngineV2)
    // CRITICAL: Read from refs to avoid re-init loop. Callbacks are wrapped in
    // trampolines that read from callbacksRef at call time (always latest).
    const currentInitData = initDataRef.current;
    if (currentInitData) {
      const cRef = callbacksRef;
      const instance = buildShadowDomSdk(currentInitData, {
        onMountPurchaseButton: (target, input) => mountPurchaseSlotRef.current(target, input),
        onNavigate: (data) => (cRef.current.onNavigate || ((d: Record<string, unknown>) => console.warn('[WebEmbed] navigateNext (no handler)', d)))(data),
        onBack: () => (cRef.current.onBack || (() => console.warn('[WebEmbed] navigateBack (no handler)')))(),
        onTrackEvent: (name, params) => (cRef.current.onTrackEvent || ((n: string, p?: Record<string, unknown>) => console.log('[WebEmbed] trackEvent:', n, p)))(name, params),
        onValueChange: (key, value) => cRef.current.onValueChange?.(key, value),
        onSetVariable: (variableId, value) => cRef.current.onSetVariable?.(variableId, value),
        onTriggerOptionAction: (request) => cRef.current.onTriggerOptionAction?.(request),
        onTriggerButtonAction: (request) => cRef.current.onTriggerButtonAction?.(request),
        onSelectProduct: (childSectionId, productIndex, productId) => cRef.current.onSelectProduct?.(childSectionId, productIndex, productId),
        onPurchaseProduct: (requestId, productId, childSectionId) => cRef.current.onPurchaseProduct?.(requestId, productId, childSectionId),
      }, shadow);

      shadowSdkInstanceRef.current = instance;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).segmentlySDK = instance.sdk;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any)[shadowIdRef.current + '__sdk'] = instance.sdk;

      // Fire segmentlySDKReady immediately (shadow DOM SDK is always ready)
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('segmentlySDKReady', { detail: initData }));
      }, 0);
    } else {
      // Preview/edit mode: minimal fallback SDK
      shadowSdkInstanceRef.current = null;
      const legacyData = {
        inputs: {},
        labels: embedAny?.outputs ? transformOutputsToLegacyFormat(embedAny.outputs) : {},
        userData: {},
        customData: {},
        webOnboarding: { onboardingId: '', versionId: undefined, screenCount: undefined },
        incomeUserData: { ip: null, userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '' },
        language: locale,
        screenId: step.id,
      };

      // Fallback SDK reads from previewDataRef at call time (trampoline pattern).
      // This ensures editing a Text section in the editor immediately reflects
      // in the Shadow DOM preview without re-initializing scripts.
      const pdRef = previewDataRef;

      const fallbackSdk = {
        isReady: () => true,
        ready: () => Promise.resolve(fallbackSdk),
        get screen() {
          return {
            screenId: step.id, screenName: '', labels: pdRef.current.labels, values: pdRef.current.values,
            locale: locale, userInputs: {}, customData: {},
          };
        },
        _shadowRoot: shadow,
        getShadowRoot: () => shadow,
        getElementById: (id: string) => shadow!.getElementById(id),
        querySelector: (sel: string) => shadow!.querySelector(sel),
        querySelectorAll: (sel: string) => shadow!.querySelectorAll(sel),
        navigateNext: (data: unknown) => console.warn('[WebEmbed] navigateNext in preview mode', data),
        navigateBack: () => console.warn('[WebEmbed] navigateBack in preview mode'),
        setValue: function(this: { screen: { values: Record<string, unknown> } }, key: string, value: unknown) {
          this.screen.values[key] = value;
          window.dispatchEvent(new CustomEvent('segmently:setValue', { detail: { key, value, screenId: step.id } }));
        },
        setVariable: (id: string, value: unknown) => console.log('[WebEmbed] setVariable (preview):', id, value),
        getVariable: (_id: string) => undefined,
        getAllVariableValues: () => ({}),
        trackEvent: (name: string, params?: Record<string, unknown>) => console.log('[WebEmbed] trackEvent (preview):', name, params),
        openUrl: (type: string) => console.log('[WebEmbed] openUrl (preview):', type),
        restorePurchases: () => console.log('[WebEmbed] restorePurchases (preview)'),
        // Child section methods — read fresh from ref at call time.
        //
        // CRITICAL: Sections MUST be addressed by id or label (NEVER by kind), because
        // multiple sections of the same kind can coexist per screen. Kind-based lookup
        // returns a random first match and causes content bleed-through between fields.
        //
        // The ONLY exception is 'ProductCatalog' — the single canonical singleton per
        // screen. All other kinds (Text, Media, BulletList, SingleSelectionList, etc.)
        // require exact id or label match and return null on kind-based lookup.
        getChildSections: () => pdRef.current.childSections,
        getChildSection: (nameOrId: string) => {
          const list = pdRef.current.childSections;
          return findCustomScreenChildSection(list, nameOrId, true);
        },
        getChildSectionText: (nameOrId: string) => {
          const list = pdRef.current.childSections;
          if (!list || !nameOrId) return null;
          const sec = findCustomScreenChildSection(list, nameOrId);
          if (!sec || (sec as { kind?: string }).kind !== 'Text') return null;
          return { title: sec.data.title || '', subtitle: sec.data.subtitle || '', subtitle2: sec.data.subtitle2 || '' };
        },
        getChildSectionMedia: (nameOrId: string) => {
          const list = pdRef.current.childSections;
          if (!list || !nameOrId) return null;
          const sec = findCustomScreenChildSection(list, nameOrId);
          if (!sec || (sec as { kind?: string }).kind !== 'Media') return null;
          return { url: sec.data.mediaUrl || '', type: sec.data.mediaType || '' };
        },
        triggerOptionAction: (sectionIdOrLabel: string, target: unknown) => {
          console.warn('[WebEmbed] triggerOptionAction in preview mode', sectionIdOrLabel, target);
        },
        triggerButtonAction: (sectionIdOrLabel: string) => {
          console.warn('[WebEmbed] triggerButtonAction in preview mode', sectionIdOrLabel);
        },
        // Product methods — return empty in preview (no Stripe products configured)
        getProducts: () => [],
        getProductPrice: () => null,
        getSelectedProduct: () => null,
        selectProduct: (_id: string) => {},
        mountPurchaseButton: (_target: unknown, _product: unknown, opts?: { onFallback?: () => void }) => {
          try { opts?.onFallback?.(); } catch { /* Preview stays inert. */ }
          return Object.assign(() => {}, { requestClose: () => {} });
        },
        purchaseProduct: (_id: string, opts?: { onError?: (e: unknown) => void }) => {
          console.warn('[WebEmbed] purchaseProduct in preview mode');
          if (opts?.onError) opts.onError({ error: 'Preview mode — no payment available' });
        },
        _notifyScriptsReady: () => window.postMessage({ eventName: 'InitCustomScreen', args: legacyData }, '*'),
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).segmentlySDK = fallbackSdk;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any)[shadowIdRef.current + '__sdk'] = fallbackSdk;

      // Fire segmentlySDKReady in preview mode too (matches iframe behavior).
      // HTML using event-based bootstrap needs this to initialize.
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('segmentlySDKReady'));
      }, 0);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).onboardingOnlineCustomScreenData = legacyData;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).leaveCustomScreen = (args: Record<string, unknown>) => { console.warn('[WebEmbed] leaveCustomScreen in preview', args); Object.assign(legacyData.inputs, args); };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).backScreen = () => console.warn('[WebEmbed] backScreen in preview');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (!(window as any).amplitude) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).amplitude = { track: (e: string, p?: Record<string, unknown>) => console.log('[WebEmbed] amplitude.track (preview):', e, p), init: () => {}, identify: () => {}, setUserId: () => {} };
      }
    }

    // Inject HTML with base styles.
    //
    // :host > * rules ONLY force stretch-fill behavior (flex:1, width:100%) so
    // the injected content div (was inside <body>) fills the shadow host.
    // Do NOT force max-width:none or margin:0 — that would break HTML that uses
    // a fixed-width mobile container pattern (e.g. `.screen-root { max-width: 390px; margin: 0 auto }`).
    // Well-authored HTML controls its own container width.
    //
    // DSD note: when `wasDSDRendered` is true, SSR already placed Custom HTML inside the
    // shadow root via `<template shadowrootmode="open">`, so re-injecting would wipe that
    // content and cause a visible flash. Scripts still need `activateScripts(shadow)`
    // below — DSD doesn't execute `<script>` tags automatically per spec.
    if (!wasDSDRendered) {
      shadow.innerHTML = `
      <style>
        :host {
          all: initial;
          display: flex;
          flex-direction: column;
          width: 100%;
          height: 100%;
          overflow: auto;
          border-radius: 0;
          /* WP8 UX M-3: the ground was a hardcoded white; it now follows the funnel's own
             canvas whenever that canvas can carry the embed's default dark text. */
          --sv2-embed-ground: ${embedColorsRef.current.ground};
          --sv2-embed-foreground: ${embedColorsRef.current.foreground};
          background: ${embedColorsRef.current.ground};
          color: ${embedColorsRef.current.foreground};
          /* inherit (not a hardcoded system stack) so document-level @font-face
             from the project's web fonts reaches shadow content; hosts without
             web fonts still resolve to the page's system default */
          font-family: inherit;
        }
        /* browser strips <html>/<body> from innerHTML — hide orphaned head elements */
        :host > meta, :host > title, :host > link[rel="preconnect"],
        :host > link[rel="icon"], :host > base {
          display: none !important;
        }
        /* main content div (was inside <body>) must fill the shadow host.
           Width:100% as a nudge (not !important — HTML may set a stricter width).
           Flex:1 ensures vertical stretch when the host has available height. */
        :host > * {
          flex: 1;
          min-height: 0;
          width: 100%;
        }
        *, *::before, *::after {
          box-sizing: border-box;
        }
      </style>
      ${rawHtml}
    `;
    }

    // Guard: only activate scripts ONCE per screen.
    // shadowScriptsActivatedRef prevents re-activation loops (purchaseProduct → state change → re-render).
    //
    // Runtime (FunnelEngineV2): wait for currentInitData before activating — scripts need full SDK
    // with getProducts/purchaseProduct. Activating early with fallback SDK would lock out the real SDK.
    //
    // Preview/edit (editor, canvas): activate immediately with fallback SDK — no runtime will provide
    // initData, so waiting would leave scripts forever dormant (black screen).
    const isPreviewOrEdit = editMode || previewMode === 'static';
    const canActivate = currentInitData || isPreviewOrEdit;

    if (!shadowScriptsActivatedRef.current && canActivate) {
      shadowScriptsActivatedRef.current = true;

      const doActivate = () => {
        if (!shadow.isConnected) return; // cleanup ran before activation
        console.log(`[WebEmbed] Shadow DOM: activating scripts ${currentInitData ? '(initData ready)' : '(preview/fallback SDK)'} step=${step.id.substring(0,8)}`);
        activateScripts(shadow, shadowIdRef.current);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((window as any).segmentlySDK?._notifyScriptsReady) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).segmentlySDK._notifyScriptsReady();
        }
      };

      if (isPreviewOrEdit) {
        // Preview/canvas: defer to next frame so shadow host has layout dimensions.
        // Without this, CSS height:100% computes to 0 on first mount.
        requestAnimationFrame(doActivate);
      } else {
        // Runtime: activate immediately (initData is ready, gesture context must be preserved).
        doActivate();
      }
    }

    const currentShadowId = shadowIdRef.current;
    const wasPreviewOrEdit = isPreviewOrEdit;
    return () => {
      shadowSdkInstanceRef.current?._disposePurchaseSlots();
      customPurchaseSlots.clear();
      if (shadowHostRef.current?.shadowRoot) {
        shadowHostRef.current.shadowRoot.innerHTML = '';
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (window as any)[currentShadowId];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (window as any)[currentShadowId + '__sdk'];
      shadowSdkInstanceRef.current = null;
      // In preview/edit: reset so next effect run re-activates scripts with fresh DOM.
      // In runtime: do NOT reset — StrictMode double-mount would cause duplicate listeners.
      if (wasPreviewOrEdit) {
        shadowScriptsActivatedRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.id, rawHtml, locale, hasRawHtml, effectiveRenderMode, hasInitData, editMode, previewMode]);

  // Deliver purchase results from host to shadow DOM SDK callbacks
  useEffect(() => {
    if (effectiveRenderMode !== 'shadow-dom' || !lastPurchaseResult || !shadowSdkInstanceRef.current) return;
    shadowSdkInstanceRef.current._deliverPurchaseResult(
      lastPurchaseResult.requestId,
      lastPurchaseResult.status as 'success' | 'cancel' | 'error',
      lastPurchaseResult.error,
    );
  }, [effectiveRenderMode, lastPurchaseResult]);

  // Deliver variable updates from host to shadow DOM SDK listeners
  useEffect(() => {
    if (effectiveRenderMode !== 'shadow-dom' || !lastVariableUpdate || !shadowSdkInstanceRef.current) return;
    shadowSdkInstanceRef.current._deliverVariableUpdate(
      lastVariableUpdate.variableId,
      lastVariableUpdate.value,
    );
  }, [effectiveRenderMode, lastVariableUpdate]);

  // Event listener for setValue from custom HTML scripts (two-way binding)
  useEffect(() => {
    if (!onValueChange) return;

    const handleSetValue = (event: Event) => {
      const customEvent = event as CustomEvent<{ key: string; value: unknown; screenId: string }>;
      const { key, value, screenId } = customEvent.detail;
      if (screenId !== step.id) return;
      onValueChange(key, value);
    };

    window.addEventListener('segmently:setValue', handleSetValue);
    return () => window.removeEventListener('segmently:setValue', handleSetValue);
  }, [step.id, onValueChange]);

  const ep = editMode || previewMode === 'live' ? 'content' : undefined;

  // Content area inline styles for struct-driven overrides
  const contentStyle: CSSProperties = hasRawHtml ? { padding: 0 } : {};

  // Memoize preview initData so IframeRenderer doesn't re-send on every render.
  // effectiveInitData (not raw initData): a stale initData from the previous WebEmbed
  // screen must never reach the iframe as its first InitCustomScreen — custom HTML
  // initializes once on segmentlySDKReady and would permanently read the wrong
  // screen's childSections (same guard as the shadow-DOM branch above).
  const iframeInitData = useMemo(
    () => effectiveInitData || createPreviewInitData(step, locale, embed),
    [effectiveInitData, step, locale, embed]
  );

  // Determine which content mode to render
  const renderContent = () => {
    // Mode 1: iframe mode (DEFAULT) - Full JS/CSS isolation
    if (hasRawHtml && effectiveRenderMode === 'iframe') {
      return (
        <IframeRenderer
          htmlContent={rawHtml}
          screenId={step.id}
          initData={iframeInitData}
          onNavigate={onNavigate || ((data) => {
            console.warn('[WebEmbed] Navigate in preview mode:', data);
          })}
          onBack={onBack || (() => {
            console.warn('[WebEmbed] Back in preview mode');
          })}
          onTrackEvent={onTrackEvent || ((name, params) => {
            console.log('[WebEmbed] Track event in preview:', name, params);
          })}
          onValueChange={onValueChange}
          onSetVariable={onSetVariable}
          onTriggerOptionAction={onTriggerOptionAction}
          onTriggerButtonAction={onTriggerButtonAction}
          onSelectProduct={onSelectProduct}
          onPurchaseProduct={onPurchaseProduct}
          lastVariableUpdate={lastVariableUpdate}
          lastPurchaseResult={lastPurchaseResult}
          className={styles.customContent}
          embedGround={embedColors.ground}
          embedForeground={embedColors.foreground}
          style={{
            flex: 1,
            minHeight: 0,
            position: 'relative',
            width: '100%',
          }}
        />
      );
    }

    // Mode 2: Shadow DOM mode - explicit request only
    if (hasRawHtml && effectiveRenderMode === 'shadow-dom') {
      // Unified shadow host element — identical JSX shape for SSR and client so that
      // React 18 hydration doesn't diff the subtree (both paths emit the same tag + attrs).
      //
      // * SSR (`ssrMode=true`): we include `dangerouslySetInnerHTML={{ __html: dsdMarkup }}`
      //   which emits `<template shadowrootmode="open">` directly into the host. The browser
      //   materialises the ShadowRoot on HTML parse — paywall content is visible before JS
      //   runs, and Apple Pay sees shadow-owned context immediately.
      //
      // * Client (hydration or pure CSR): `dangerouslySetInnerHTML` is undefined, so React
      //   doesn't touch the host's existing children. Because `suppressHydrationWarning` is
      //   set on every render, hydration never tries to reconcile `<template shadowrootmode>`
      //   (which React 18 can't handle — https://github.com/facebook/react/issues/26071).
      //   The shadow content survives hydration untouched → no flash, no re-render storm.
      //
      // The client-side useEffect still runs post-hydration:
      //   - `host.shadowRoot` already populated (DSD)          → skip innerHTML re-injection
      //   - `host.shadowRoot` null + `<template>` left behind  → Safari 16.3- fallback
      //   - `host.shadowRoot` null + no template                → pure CSR, attachShadow + inject
      // The ground/foreground pair and its tokens are declared on `:host`, ahead of the
      // Custom HTML, so any style the author wrote still wins (WP8 UX M-3).
      const shadowHostStyles =
        ':host{all:initial;display:flex;flex-direction:column;width:100%;height:100%;box-sizing:border-box;'
        + `--sv2-embed-ground:${embedColors.ground};--sv2-embed-foreground:${embedColors.foreground};`
        + `background-color:${embedColors.ground};color:${embedColors.foreground}}`;
      const dsdInitial = ssrMode
        ? {
            __html: `<template shadowrootmode="open"><style>${shadowHostStyles}</style>${rawHtml}</template>`,
          }
        : undefined;
      return (
        <div
          ref={shadowHostRef}
          className={styles.customContent}
          data-testid="view-web-embed-shadow-host"
          data-dsd="true"
          tabIndex={0}
          style={{
            flex: 1,
            minHeight: 0,
            position: 'relative',
            width: '100%',
            pointerEvents: 'auto',
          }}
          suppressHydrationWarning
          {...(dsdInitial ? { dangerouslySetInnerHTML: dsdInitial } : {})}
        />
      );
    }

    // Mode 3: Custom render callback (React-based custom content)
    if (customRender) {
      return customRender(step, locale);
    }

    // Mode 4: Fallback placeholder
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          color: '#666',
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
        }}
        data-testid="view-web-embed-fallback"
      >
        <div style={{ fontSize: 48, marginBottom: 16 }}>&#127912;</div>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
          Web Embed
        </div>
        <div style={{ fontSize: 14, color: '#999' }}>
          Provide HTML content or a customRender callback to display custom content
        </div>
      </div>
    );
  };

  if (embedded) return <>{renderContent()}{customPurchaseSlots.portals}</>;

  return (
    <div
      className={[
        styles.container,
        editMode && 'screen-view--edit-mode',
        showHeader && styles.paddingTop,
        showActionBar && styles.paddingBottom,
        previewMode === 'live' && styles.liveMode,
      ].filter(Boolean).join(' ')}
      data-testid="view-web-embed"
    >
      <Background
        backdrop={content?.canvas?.background}
        locale={locale}
        editPath={ep ? `${ep}.canvas.background` : undefined}
        previewMode={previewMode}
      />

      {showHeader && (
        <HeaderBar
          header={content?.header}
          locale={locale}
          variables={variables}
          step={step}
          onAction={onAction}
          editPath={ep ? `${ep}.header` : undefined}
        />
      )}

      <div className={styles.content} style={contentStyle}>
        {renderContent()}
      </div>
      {customPurchaseSlots.portals}

      {showActionBar && (
        <ActionBar
          actionBar={content?.actionBar}
          locale={locale}
          variables={variables}
          step={step}
          onAction={onAction}
          editPath={ep ? `${ep}.actionBar` : undefined}
        />
      )}
    </div>
  );
});

WebEmbed.displayName = 'WebEmbed';
